<!-- Chat form with an interactive slider for picking a value on a numeric scale. -->

<script lang="ts" setup>
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useEmitAsync } from '@nzyme/vue-utils/useEmitAsync.js';
import { computed, ref } from 'vue';

import type { ScaleInputConfig, ScaleInputValue } from '@sigma/common/forms/ScaleInput.js';

import Button from '../../Button.vue';
import FieldInfo from '../../forms/FieldInfo.vue';
import * as l from './ChatScaleInput.loc.js';

interface Props {
  config: ScaleInputConfig;
}

interface Emits {
  submit: [value: ScaleInputValue];
}

const props = defineProps<Props>();
defineEmits<Emits>();

const t = useTranslate();
const emitAsync = useEmitAsync<Emits>();

const error = ref<string | null>(null);
const selectedValue = ref<number | null>(null);
const hoveredValue = ref<number | null>(null);
const sliderContainerRef = ref<HTMLElement | null>(null);

// Thumb size must match CSS $thumb-size
const THUMB_SIZE = 28;

const scaleValues = computed(() => {
  const values: number[] = [];
  const { minValue, maxValue, step } = props.config;
  for (let v = minValue; v <= maxValue; v += step) {
    values.push(v);
  }
  return values;
});

/**
 * Calculate the progress percentage for the filled track.
 */
const progressPercent = computed(() => {
  if (selectedValue.value === null) {
    return 0;
  }
  const { minValue, maxValue } = props.config;
  return ((selectedValue.value - minValue) / (maxValue - minValue)) * 100;
});

/**
 * Calculate the left position for a slot to align with where the native range thumb lands.
 * Native range input positions thumb center at: (value - min) / (max - min) * trackWidth
 * But the track is inset by half thumb width on each side.
 */
function getSlotPosition(value: number): string {
  const { minValue, maxValue } = props.config;
  const percent = (value - minValue) / (maxValue - minValue);
  // Account for thumb width: at 0% thumb center is at half-thumb from left edge
  // at 100% thumb center is at half-thumb from right edge
  // So we need: halfThumb + percent * (100% - thumbSize)
  return `calc(${THUMB_SIZE / 2}px + ${percent} * (100% - ${THUMB_SIZE}px))`;
}

/**
 * Calculate value from click X position within the container.
 */
function getValueFromPosition(clientX: number): number {
  const container = sliderContainerRef.value;
  if (!container) {
    return props.config.minValue;
  }

  const rect = container.getBoundingClientRect();
  const x = clientX - rect.left;
  const width = rect.width;

  // Reverse the getSlotPosition calculation
  // position = thumbSize/2 + percent * (width - thumbSize)
  // percent = (position - thumbSize/2) / (width - thumbSize)
  const percent = Math.max(0, Math.min(1, (x - THUMB_SIZE / 2) / (width - THUMB_SIZE)));

  const { minValue, maxValue, step } = props.config;
  const rawValue = minValue + percent * (maxValue - minValue);

  // Round to nearest step
  const steppedValue = Math.round((rawValue - minValue) / step) * step + minValue;
  return Math.max(minValue, Math.min(maxValue, steppedValue));
}

function handleContainerClick(event: MouseEvent) {
  // Don't handle if clicking on the range input (it handles itself)
  if ((event.target as HTMLElement).tagName === 'INPUT') {
    return;
  }

  selectedValue.value = getValueFromPosition(event.clientX);
}

function handleContainerMouseMove(event: MouseEvent) {
  hoveredValue.value = getValueFromPosition(event.clientX);
}

function handleContainerMouseLeave() {
  hoveredValue.value = null;
}

function handleSliderInput(event: Event) {
  const target = event.target as HTMLInputElement;
  selectedValue.value = Number(target.value);
}

async function handleSubmit() {
  if (selectedValue.value === null) {
    error.value = t(l.selectValue);
    return;
  }

  error.value = null;

  await emitAsync('submit', {
    type: 'SCALE',
    value: selectedValue.value,
  });
}
</script>

<template>
  <Form
    :class="css.formContainer"
    @submit="handleSubmit"
  >
    <!-- Clickable container for the entire slider area -->
    <div
      ref="sliderContainerRef"
      :class="css.sliderContainer"
      @click="handleContainerClick"
      @mousemove="handleContainerMouseMove"
      @mouseleave="handleContainerMouseLeave"
    >
      <!-- Slider wrapper for positioning -->
      <div :class="css.sliderWrapper">
        <!-- Visual slots layer (behind the input) -->
        <div :class="css.slotsLayer">
          <!-- Progress track (filled portion) -->
          <div
            :class="css.progressTrack"
            :style="{ width: `calc((100% - ${THUMB_SIZE}px) * ${progressPercent / 100})` }"
          />

          <div
            v-for="val in scaleValues"
            :key="val"
            :class="css.slotWrapper"
            :style="{ left: getSlotPosition(val) }"
          >
            <div
              :class="[
                css.slot,
                selectedValue !== null && val < selectedValue && css.slotFilled,
                selectedValue === val && css.slotActive,
                hoveredValue === val && selectedValue !== val && css.slotHovered,
              ]"
            />
          </div>
        </div>

        <!-- Native range input -->
        <input
          type="range"
          :min="config.minValue"
          :max="config.maxValue"
          :step="config.step"
          :value="selectedValue ?? config.minValue"
          :class="[css.rangeInput, selectedValue === null && css.rangeInputHidden]"
          :aria-label="config.minLabel ? `${config.minLabel} - ${config.maxLabel}` : t(l.scaleLabel)"
          :aria-valuetext="selectedValue === null ? t(l.noSelection) : undefined"
          @input="handleSliderInput"
        />
      </div>

      <!-- Values row -->
      <div :class="css.valuesRow">
        <span
          v-for="val in scaleValues"
          :key="val"
          :class="[
            css.valueLabel,
            selectedValue === val && css.valueLabelSelected,
            hoveredValue === val && css.valueLabelHovered,
          ]"
          :style="{ left: getSlotPosition(val) }"
        >
          {{ val }}
        </span>
      </div>

      <!-- Labels row: low, middle, high -->
      <div
        v-if="config.minLabel || config.middleLabel || config.maxLabel"
        :class="css.labelsRow"
      >
        <span :class="css.labelLow">{{ config.minLabel }}</span>
        <span :class="css.labelMiddle">{{ config.middleLabel }}</span>
        <span :class="css.labelHigh">{{ config.maxLabel }}</span>
      </div>
    </div>

    <FieldInfo
      v-if="error"
      type="error"
    >
      {{ error }}
    </FieldInfo>

    <Button
      tid="chat-question-submit"
      variant="primary"
      size="large"
      color="black"
      rounded
      :class="css.submitButton"
      type="submit"
    >
      {{ t(l.submitButton) }}
    </Button>
  </Form>
</template>

<style lang="scss" module="css">
// Sizing
$thumb-size: 28px;
$slot-size: 16px;
$track-height: 3px;
$slot-border: 3px;

// Colors
$track-color: $color-gray-200;
$selected-color: $color-doctor-600;
$slot-bg-color: $color-white;
$thumb-border-color: $color-white;
$label-color: $color-gray-600;
$label-hover-color: $color-gray-700;
$label-selected-color: $color-doctor-600;

.formContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-08;
}

.sliderContainer {
  display: flex;
  flex-direction: column;
  gap: $spacing-04;
  // Extra padding to increase clickable area
  padding: $spacing-12 0;
  cursor: pointer;
}

.sliderWrapper {
  position: relative;
  height: $thumb-size;
}

.slotsLayer {
  position: absolute;
  inset: 0;
  pointer-events: none;

  // Background track
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    // Inset by half thumb size to match native range track
    left: calc($thumb-size / 2);
    right: calc($thumb-size / 2);
    height: $track-height;
    background-color: $track-color;
    border-radius: calc($track-height / 2);
    transform: translateY(-50%);
  }
}

.progressTrack {
  position: absolute;
  top: 50%;
  // Start where the track starts (inset by half thumb)
  left: calc($thumb-size / 2);
  height: $track-height;
  background-color: $selected-color;
  border-radius: calc($track-height / 2);
  transform: translateY(-50%);
  transition: width 0.2s ease-out;
  z-index: 1;
}

.slotWrapper {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
}

.slot {
  width: $slot-size;
  height: $slot-size;
  border-radius: 50%;
  background-color: $slot-bg-color;
  border: $slot-border solid $track-color;
  transition:
    border-color 0.2s ease-out,
    background-color 0.2s ease-out,
    transform 0.2s ease-out,
    opacity 0.2s ease-out;
}

.slotFilled {
  border-color: $selected-color;
}

.slotHovered {
  transform: scale(1.25);
}

.slotActive {
  // Hide the slot when it's under the thumb
  opacity: 0;
}

.rangeInput {
  position: relative;
  width: 100%;
  height: $thumb-size;
  background: transparent;
  cursor: pointer;
  margin: 0;
  z-index: 3;

  // Remove default styles
  -webkit-appearance: none;
  appearance: none;

  &:focus {
    outline: none;
  }

  // Webkit (Chrome, Safari, Edge)
  &::-webkit-slider-runnable-track {
    width: 100%;
    height: $track-height;
    background: transparent;
    border-radius: calc($track-height / 2);
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: $thumb-size;
    height: $thumb-size;
    border-radius: 50%;
    background-color: $selected-color;
    border: 3px solid $thumb-border-color;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    cursor: grab;
    margin-top: calc(($track-height - $thumb-size) / 2);
    transition:
      transform 0.2s ease-out,
      opacity 0.2s ease-out;

    &:active {
      cursor: grabbing;
      transform: scale(1.1);
    }
  }

  &:focus::-webkit-slider-thumb {
    box-shadow:
      0 2px 6px rgba(0, 0, 0, 0.2),
      0 0 0 4px rgba($selected-color, 0.2);
  }

  // Firefox
  &::-moz-range-track {
    width: 100%;
    height: $track-height;
    background: transparent;
    border-radius: calc($track-height / 2);
  }

  &::-moz-range-thumb {
    width: $thumb-size;
    height: $thumb-size;
    border-radius: 50%;
    background-color: $selected-color;
    border: 3px solid $thumb-border-color;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    cursor: grab;
    transition: transform 0.1s ease-out;

    &:active {
      cursor: grabbing;
      transform: scale(1.1);
    }
  }

  &:focus::-moz-range-thumb {
    box-shadow:
      0 2px 6px rgba(0, 0, 0, 0.2),
      0 0 0 4px rgba($selected-color, 0.2);
  }
}

.rangeInputHidden {
  // Hide thumb when no value is selected
  &::-webkit-slider-thumb {
    opacity: 0;
    pointer-events: none;
  }

  &::-moz-range-thumb {
    opacity: 0;
    pointer-events: none;
  }
}

.valuesRow {
  position: relative;
  height: 20px;
}

.valueLabel {
  position: absolute;
  transform: translateX(-50%);
  font-size: $font-size-14;
  font-weight: $font-weight-500;
  color: $label-color;
  user-select: none;
  transition: color 0.15s ease-out;
}

.valueLabelHovered {
  color: $label-hover-color;
}

.valueLabelSelected {
  color: $label-selected-color;
  font-weight: $font-weight-600;
}

.labelsRow {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: $spacing-08;
  padding-top: $spacing-04;
  padding-inline: $spacing-08;
}

.labelLow {
  font-size: $font-size-12;
  color: $label-color;
  text-align: left;
  user-select: none;
}

.labelMiddle {
  font-size: $font-size-12;
  color: $label-color;
  text-align: center;
  user-select: none;
}

.labelHigh {
  font-size: $font-size-12;
  color: $label-color;
  text-align: right;
  user-select: none;
}

.submitButton {
  margin-top: $spacing-08;
}
</style>
